import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { useHttpClient } from "@/providers/HttpClient";
import {
  ContentCopyIcon,
  DownloadIcon,
  OpenInNewIcon,
} from "@/shared/foundation/icons/mui";
import { robotoHeaders } from "@/shared/http";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./BYOB.module.css";

// AWS regions selectable in the BYOB admin form (commercial + GovCloud)
const AWS_REGIONS = [
  "us-east-1",
  "us-east-2",
  "us-west-1",
  "us-west-2",
  "us-gov-west-1",
  "eu-west-1",
  "eu-west-2",
  "eu-central-1",
  "ap-northeast-1",
  "ap-southeast-1",
  "ap-southeast-2",
] as const;

const byobFormSchema = z.object({
  accountId: z.string().min(1, "AWS Account ID is required"),
  awsRegion: z.string().min(1, "AWS Region is required"),
  orgId: z.string().min(1, "Org ID is required"),
  bucketName: z.string().min(1, "Bucket Name is required"),
  accessType: z.enum(["readonly", "readwrite"]),
});

type BYOBFormData = z.infer<typeof byobFormSchema>;

interface RegisterS3IntegrationRequest {
  account_id: string;
  aws_region: string;
  bucket_name: string;
  org_id: string;
  transfer_accelerated: boolean;
  readonly: boolean;
}

interface RegisterS3IntegrationResponse {
  iam_role_name: string;
  iam_role_policy: Record<string, unknown>;
  iam_role_trust_relationship: Record<string, unknown>;
  s3_bucket_cors_policy: Record<string, unknown>[];
}

const BYOB_GUIDE_URL =
  "https://github.com/roboto-ai/roboto-hosted-app/blob/main/docs/guides/backend/add-bring-your-own-bucket.md";

interface CopyableJsonBlockProps {
  title: string;
  data: unknown;
}

function CopyableJsonBlock({ title, data }: CopyableJsonBlockProps) {
  const theme = useTheme();
  const [copied, setCopied] = React.useState(false);
  const jsonString = JSON.stringify(data, null, 2);

  const handleCopy = () => {
    void navigator.clipboard.writeText(jsonString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={styles.jsonBlockContainer}>
      <div className={styles.jsonBlockHeader}>
        <Typography variant="subtitle2" fontWeight="bold">
          {title}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
          <IconButton size="small" onClick={handleCopy}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      <Paper
        variant="outlined"
        className={styles.jsonBlockPaper}
        sx={{
          backgroundColor:
            theme.palette.code?.main || theme.palette.background.paper,
        }}
      >
        <Typography component="pre" className={styles.jsonBlockCode}>
          {jsonString}
        </Typography>
      </Paper>
    </div>
  );
}

export function BYOB() {
  const { httpClient } = useHttpClient();

  const form = useForm<BYOBFormData>({
    defaultValues: {
      accountId: "",
      awsRegion: "us-west-2",
      orgId: "",
      bucketName: "",
      accessType: "readonly",
    },
    resolver: zodResolver(byobFormSchema),
  });

  const [response, setResponse] =
    React.useState<RegisterS3IntegrationResponse | null>(null);

  const onSubmit: SubmitHandler<BYOBFormData> = async (data) => {
    setResponse(null);

    try {
      const url = httpClient.constructUrl(
        "v1/integrations/storage/s3/register",
      );
      const requestBody: RegisterS3IntegrationRequest = {
        account_id: data.accountId,
        aws_region: data.awsRegion,
        bucket_name: data.bucketName,
        org_id: data.orgId,
        transfer_accelerated: false,
        readonly: data.accessType === "readonly",
      };

      const httpResponse = await httpClient.post(url, {
        body: JSON.stringify(requestBody),
        headers: robotoHeaders({ orgId: data.orgId }),
      });

      const responseData =
        await httpResponse.json<RegisterS3IntegrationResponse>();
      setResponse(responseData);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to register S3 integration";
      form.setError("root.serverError", { message: errorMessage });
      ErrorMonitoringService.captureError(err);
    }
  };

  const handleDownloadAll = () => {
    if (!response) {
      return;
    }

    const formValues = form.getValues();
    const blob = new Blob([JSON.stringify(response, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `byob-${formValues.bucketName}-${formValues.accessType}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const serverError = form.formState.errors.root?.serverError;

  return (
    <div className={styles.container}>
      {/* Header with link to guide */}
      <div className={styles.header}>
        <Typography variant="h6">Register Bring-Your-Own-Bucket</Typography>
        <Link
          href={BYOB_GUIDE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.guideLink}
        >
          <Typography variant="body2">Setup Guide</Typography>
          <OpenInNewIcon fontSize="small" />
        </Link>
      </div>

      {/* Input Form */}
      <Paper variant="outlined" className={styles.formPaper}>
        <form
          onSubmit={(event) => {
            form
              .handleSubmit(onSubmit)(event)
              .catch((e) => {
                ErrorMonitoringService.captureError(e);
              });
          }}
        >
          <div className={styles.formContent}>
            <Controller
              control={form.control}
              name="accountId"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="AWS Account ID"
                  placeholder="123456789012"
                  size="small"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              control={form.control}
              name="awsRegion"
              render={({ field, fieldState: { error } }) => (
                <FormControl size="small" fullWidth error={!!error}>
                  <InputLabel>AWS Region</InputLabel>
                  <Select {...field} label="AWS Region">
                    {AWS_REGIONS.map((region) => (
                      <MenuItem key={region} value={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              control={form.control}
              name="orgId"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Org ID"
                  placeholder="og_xxxxxxxxxxxx"
                  size="small"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              control={form.control}
              name="bucketName"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Bucket Name"
                  placeholder="my-company-data-bucket"
                  size="small"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              control={form.control}
              name="accessType"
              render={({ field }) => (
                <FormControl>
                  <Typography
                    variant="body2"
                    className={styles.accessTypeLabel}
                  >
                    Access Type
                  </Typography>
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value="readonly"
                      control={<Radio size="small" />}
                      label="Read-Only"
                    />
                    <FormControlLabel
                      value="readwrite"
                      control={<Radio size="small" />}
                      label="Read-Write"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={form.formState.isSubmitting}
              className={styles.submitButton}
            >
              {form.formState.isSubmitting ? (
                <CircularProgress size={20} />
              ) : (
                "Register Bucket"
              )}
            </Button>
          </div>
        </form>
      </Paper>

      {/* Error Display */}
      {serverError && (
        <Alert severity="error" className={styles.errorAlert}>
          {serverError.message}
        </Alert>
      )}

      {/* Response Display */}
      {response && (
        <Paper variant="outlined" className={styles.responsePaper}>
          <div className={styles.responseHeader}>
            <Typography variant="h6">Registration Response</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadAll}
            >
              Download All
            </Button>
          </div>

          <Alert severity="success" className={styles.successAlert}>
            Bucket registered successfully! Share the following with the
            customer.
          </Alert>

          <CopyableJsonBlock
            title="IAM Role Name"
            data={response.iam_role_name}
          />
          <CopyableJsonBlock
            title="IAM Role Policy"
            data={response.iam_role_policy}
          />
          <CopyableJsonBlock
            title="IAM Role Trust Relationship"
            data={response.iam_role_trust_relationship}
          />
          <CopyableJsonBlock
            title="S3 Bucket CORS Policy"
            data={response.s3_bucket_cors_policy}
          />
        </Paper>
      )}
    </div>
  );
}
